import { AxiosRequestConfig, AxiosResponse } from "axios";
import { ODataMultipleResponse } from "@/models/responses/odata.response.interface";
import { BranchResponse } from "@/models/responses/branch.response.interface";
import { CatalogueResponse, IncomeTypeResponse, OcupationsResponse, EconomicActivityResponse, AgreementDocuments, StatusAttributeResponse } from "@/models/responses/catalogue.response.interface";
import { DocumentKindResponse } from "@/models/responses/documentation.interface";
import { PurposeResponse } from "@/models/responses/purpose.interface";
import { ApiService } from "./api.service";
import { SUSCRIPTION_KEY } from "@/constants/config";

interface FinalidadODataResponse {
  rs_finalidadid: string;
  rs_nombrefinalidad: string;
  rs_montominimo: number;
  rs_montomaximo: number;
  rs_plazominimo: number;
  rs_plazomaximo: number;
  rs_tasaordanual: number;
  //se agregan para imss
  rs_idfinalidad: number;
}

interface TipoDocumentoODataResponse {
  rs_tipodocumentoid: string;
  rs_name: string;
  rs_id: string;
  _rs_grupodocumentoid_value: string;
}

interface TipoIngresoODataResponse {
  rs_tipodeingresoid: string;
  rs_name: string;
  rs_tasaordinariaingreso: number;
}

interface GrupoDocumentoODataResponse {
  rs_grupodocumentoid: string;
  rs_name: string;
}

interface NivelEstudioODataResponse {
  rs_nivelestudiosid: string;
  rs_name: string;
}

interface ProfesionODataResponse {
  rs_profesionid: string;
  rs_name: string;
}

interface OcupacionODataResponse {
  rs_ocupacionid: string;
  rs_name: string;
}

interface OcupacionOEODataResponse {
  rs_ocupacionid: string;
  rs_name: string;
  _rs_puestoid_value: string;
  _rs_actividadeconomicaid_value: string;
  _rs_sectoreconomicoid_value: string;
}

interface EmpleoODataResponse {
  rs_puestoid: string;
  rs_name: string;
}

interface TipoViviendaODataResponse {
  rs_tipodeviviendaid: string;
  rs_name: string;
}

interface RegimenMatrimonialODataResponse {
  rs_regimenmatrimonialid: string;
  rs_name: string;
}

interface ActividadEconomicaODataResponse {
  rs_actividadeconomicaid: string;
  rs_name: string;
  _rs_sectoreconomicoid_value: string;
  _rs_actividadbanxicoid_value: string;
  _rs_ocupacionid_value: string;
  _rs_puestoid_value: string;
}

interface SectorEconomicoODataResponse {
  rs_sectoreconomicoid: string;
  rs_name: string;
}

interface CountriesODataResponse {
  rs_paisid: string;
  rs_name: string;
}

interface SucursalODataResponse {
  rs_sucursalid: string;
  rs_direccion: string;
  _rs_municipioid_value: string;
  _rs_entidadfederativa_value: string;
  rs_name: string;
  rs_idsucursal: string;
}

interface BanksODataResponse {
  rs_bancosid: string;
  rs_name: string;
}

interface ProductsODataResponse {
  productid: string;
  name: string;
}

interface BanxicoODataResponse {
  rs_actividadbanxicoid: string;
  rs_name: string;
}

interface ADODataResponse {
  rs_id: string;
  rs_name: string;
}

interface AgreementDocumentsODataResponse {
  rs_diasvigencia: number;
  _rs_tipoconvenioid_value: string;
  _rs_tipodocumentoid_value: string;
  _rs_tipotramiteid_value: string;
}

export interface StatusAttributeODataResponse {
  OptionSet: OptionSetResponse;
}

export interface OptionSetResponse {
  Options: OptionsResponse[];
}

export interface OptionsResponse {
  Value: number;
  Label: LabelResponse;
}

export interface LabelResponse {
  UserLocalizedLabel: UserLocalizedLabelResponse;
}

export interface UserLocalizedLabelResponse {
  Label: string;
}

class CatalogueService extends ApiService {
  public constructor(config?: AxiosRequestConfig) {
    super(config);

    this.getAllPurposes = this.getAllPurposes.bind(this);
    this.getAllIncomeProofs = this.getAllIncomeProofs.bind(this);
    this.getAllIncomeTypes = this.getAllIncomeTypes.bind(this);
    this.getAllEducationLevels = this.getAllEducationLevels.bind(this);
    this.getAllProfessions = this.getAllProfessions.bind(this);
    this.getAllOccupations = this.getAllOccupations.bind(this);
    this.getOccupations = this.getOccupations.bind(this);
    this.getAllJobTitles = this.getAllJobTitles.bind(this);
    this.getAllPropertyKinds = this.getAllPropertyKinds.bind(this);
    this.getAllMatrimonialRegimes = this.getAllMatrimonialRegimes.bind(this);
    this.getAllEconomicActivities = this.getAllEconomicActivities.bind(this);
    this.getAllEconomicSectors = this.getAllEconomicSectors.bind(this);
    this.getAllBranches = this.getAllBranches.bind(this);
    this.getBranchesByCity = this.getBranchesByCity.bind(this);
    this.getAllCountries = this.getAllCountries.bind(this);
    this.getAllDocumentKinds = this.getAllDocumentKinds.bind(this);
    this.getProductById = this.getProductById.bind(this);
    this.getAllBanks = this.getAllBanks.bind(this);
    this.getAllProducts = this.getAllProducts.bind(this);
  }

  /**
   * Get a list of Credit Purposes
   *
   * @returns {Promise<PurposeResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllPurposes(): Promise<PurposeResponse[]> {
    // use odata-query?
    // https://github.com/techniq/odata-query
    // https://github.com/delegateas/XrmDefinitelyTyped
    const res = await this.get<PurposeResponse, AxiosResponse<ODataMultipleResponse<FinalidadODataResponse[]>>>(
      "d365api/v1/rs_finalidads?$select=rs_finalidadid,rs_nombrefinalidad,rs_montominimo,rs_montomaximo,rs_plazominimo,rs_plazomaximo,rs_tasaordanual,rs_idfinalidad&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: PurposeResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_finalidadid,
        name: elem.rs_nombrefinalidad,
        minAmount: elem.rs_montominimo,
        maxAmount: elem.rs_montomaximo,
        minTerm: elem.rs_plazominimo,
        maxTerm: elem.rs_plazomaximo,
        ordinaryAnnualRate: elem.rs_tasaordanual,
        idfinalidad: elem.rs_idfinalidad
      });
    });
    return data;
  }

  /**
   * Get a list of Income Proofs
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllIncomeProofs(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<TipoDocumentoODataResponse[]>>>(
      "d365api/v1/rs_tipodocumentos?$select=rs_tipodocumentoid,rs_name&$filter=rs_mostrar eq 2 and statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_tipodocumentoid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Income Types
   *
   * @returns {Promise<IncomeTypeResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllIncomeTypes(): Promise<IncomeTypeResponse[]> {
    const res = await this.get<IncomeTypeResponse, AxiosResponse<ODataMultipleResponse<TipoIngresoODataResponse[]>>>(
      "d365api/v1/rs_tipodeingresos?$select=rs_tipodeingresoid,rs_name,rs_tasaordinariaingreso&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: IncomeTypeResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_tipodeingresoid,
        name: elem.rs_name,
        ordinaryRate: elem.rs_tasaordinariaingreso
      });
    });
    return data;
  }

  /**
   * Get a list of Education Levels
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllEducationLevels(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<NivelEstudioODataResponse[]>>>(
      "d365api/v1/rs_nivelestudioses?$select=rs_nivelestudiosid,rs_name&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_nivelestudiosid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Professions
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllProfessions(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<ProfesionODataResponse[]>>>("d365api/v1/rs_profesions?$select=rs_profesionid,rs_name&$filter=statecode eq 0", {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_profesionid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Occupations
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllOccupations(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<OcupacionODataResponse[]>>>("d365api/v1/rs_ocupacions?$select=rs_ocupacionid,rs_name&$filter=statecode eq 0", {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_ocupacionid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Occupations
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getOccupations(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<OcupacionODataResponse[]>>>(
      "d365api/v1/rs_ocupacions?$select=rs_ocupacionid,rs_name&$filter=statecode eq 0 and rs_idocupacion ne null",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_ocupacionid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Job Titles
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllJobTitles(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<EmpleoODataResponse[]>>>("d365api/v1/rs_puestos?$select=rs_puestoid,rs_name&$filter=statecode eq 0", {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_puestoid,
        name: elem.rs_name
      });
    });
    return data;
  }

  public async getAllOcupationsOE(): Promise<OcupationsResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<OcupacionOEODataResponse[]>>>(
      "d365api/v1/rs_ocupacions?$select=rs_ocupacionid,rs_name,_rs_puestoid_value,_rs_actividadeconomicaid_value,_rs_sectoreconomicoid_value&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: OcupationsResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_ocupacionid,
        name: elem.rs_name,
        puesto: elem._rs_puestoid_value,
        acteconomica: elem._rs_actividadeconomicaid_value,
        secteconomico: elem._rs_sectoreconomicoid_value
      });
    });
    return data;
  }

  /**
   * Get a list of Property Kinds
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllPropertyKinds(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<TipoViviendaODataResponse[]>>>(
      "d365api/v1/rs_tipodeviviendas?$select=rs_tipodeviviendaid,rs_name&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_tipodeviviendaid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Matrimonial Regimes
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllMatrimonialRegimes(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<RegimenMatrimonialODataResponse[]>>>(
      "d365api/v1/rs_regimenmatrimonials?$select=rs_regimenmatrimonialid,rs_name&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_regimenmatrimonialid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Economic Activities
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllEconomicActivities(): Promise<EconomicActivityResponse[]> {
    //https://construccion.crm.dynamics.com/api/data/v9.2/rs_actividadeconomicas?$select=rs_actividadeconomicaid,_rs_actividadbanxicoid_value,_rs_ocupacionid_value,_rs_puestoid_value,_rs_sectoreconomicoid_value,rs_name&$filter=(rs_name eq )
    const res = await this.get<EconomicActivityResponse, AxiosResponse<ODataMultipleResponse<ActividadEconomicaODataResponse[]>>>(
      "d365api/v1/rs_actividadeconomicas?$select=rs_actividadeconomicaid,_rs_actividadbanxicoid_value,_rs_ocupacionid_value,_rs_puestoid_value,_rs_sectoreconomicoid_value,rs_name&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: EconomicActivityResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_actividadeconomicaid,
        name: elem.rs_name,
        economicSector: elem._rs_sectoreconomicoid_value,
        acitivityBanxico: elem._rs_actividadbanxicoid_value,
        occupation: elem._rs_ocupacionid_value,
        position: elem._rs_puestoid_value
      });
    });
    return data;
  }

  /**
   * Get a list of Economic Sectors
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllEconomicSectors(): Promise<CatalogueResponse[]> {
    //d365api/v1/rs_sectoreconomicos?$select=rs_sectoreconomicoid,rs_name&$filter=(rs_visibilidad eq 1 or rs_visibilidad eq 3) and (statecode eq 0)
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<SectorEconomicoODataResponse[]>>>(
      "d365api/v1/rs_sectoreconomicos?$select=rs_sectoreconomicoid,rs_name&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_sectoreconomicoid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Branches
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllBranches(): Promise<BranchResponse[]> {
    const res = await this.get<BranchResponse, AxiosResponse<ODataMultipleResponse<SucursalODataResponse[]>>>(
      "d365api/v1/rs_sucursals?$select=rs_sucursalid,rs_direccion,_rs_municipioid_value,_rs_entidadfederativa_value,rs_name,rs_idsucursal&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: BranchResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_sucursalid,
        name: elem.rs_direccion,
        cityId: elem._rs_municipioid_value,
        stateId: elem._rs_entidadfederativa_value,
        branch: elem.rs_name,
        code: elem.rs_idsucursal
      });
    });
    return data;
  }

  /**
   * Get a list of Branches filtered by City ID.
   *
   * @param {string} cityId
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getBranchesByCity(cityId: Readonly<string>): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<SucursalODataResponse[]>>>(
      `d365api/v1/rs_sucursals?$select=rs_sucursalid,rs_direccion&$filter=statecode eq 0 and rs_municipioid/rs_municipioid eq ('${cityId}')`,
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_sucursalid,
        name: elem.rs_direccion
      });
    });
    return data;
  }

  /**
   * Get a list of Education Levels
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllCountries(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<CountriesODataResponse[]>>>("d365api/v1/rs_paises?$select=rs_paisid,rs_name&$filter=statecode eq 0", {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_paisid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Document Kinds
   *
   * @returns {Promise<DocumentKindResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllDocumentKinds(): Promise<DocumentKindResponse[]> {
    const gres = await this.get<GrupoDocumentoODataResponse, AxiosResponse<ODataMultipleResponse<GrupoDocumentoODataResponse[]>>>(
      "d365api/v1/rs_grupodocumentos?$select=rs_grupodocumentoid,rs_name&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );

    const res = await this.get<TipoDocumentoODataResponse, AxiosResponse<ODataMultipleResponse<TipoDocumentoODataResponse[]>>>(
      "d365api/v1/rs_tipodocumentos?$select=rs_tipodocumentoid,rs_name,rs_id,_rs_grupodocumentoid_value&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: DocumentKindResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_tipodocumentoid,
        code: elem.rs_id,
        name: elem.rs_name,
        group: gres.data.value.find((g) => g.rs_grupodocumentoid === elem._rs_grupodocumentoid_value)?.rs_name,
        groupId: elem._rs_grupodocumentoid_value
      });
    });
    return data;
  }

  /**
   * Get a Product by its Id
   *
   * @param {string} id
   * @returns {Promise<CatalogueResponse>}
   * @memberof CatalogueService
   */
  public async getProductById(id: Readonly<string>): Promise<CatalogueResponse> {
    const res = await this.get<{ productid: string; name: string }, AxiosResponse<{ productid: string; name: string }>>(`d365api/v1/products(${id})`, {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });
    return {
      id: res.data.productid,
      name: res.data.name
    };
  }

  /**
   * Get a list of Banks
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllBanks(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<BanksODataResponse[]>>>("d365api/v1/rs_bancoses?$select=rs_bancosid,rs_name&$filter=statecode eq 0", {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_bancosid,
        name: elem.rs_name
      });
    });
    return data;
  }

  /**
   * Get a list of Products
   *
   * @returns {Promise<CatalogueResponse[]>}
   * @memberof CatalogueService
   */
  public async getAllProducts(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<ProductsODataResponse[]>>>(
      "d365api/v1/products?$select=productid,name&$filter=statecode eq 0 and rs_tipoproyecto eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.productid,
        name: elem.name
      });
    });
    return data;
  }

  public async getAllBanxicoAct(): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<BanxicoODataResponse[]>>>(
      "d365api/v1/rs_actividadbanxicos?$select=rs_actividadbanxicoid,rs_name&$filter=statecode eq 0",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_actividadbanxicoid,
        name: elem.rs_name
      });
    });
    return data;
  }

  public async getAllAditionalDocuments(agreementTypeId: string): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<ADODataResponse[]>>>(
      "d365api/v1/rs_tipodocumentos?$select=rs_id,rs_name&$expand=rs_tipodocumento_rs_parametrizaciondocumentoconvenio_tipodocumentoid" +
        "($filter=(_rs_tipoconvenioid_value eq " +
        agreementTypeId +
        " and _rs_tipotramiteid_value eq null))&" +
        "$filter=(rs_tipodocumento_rs_parametrizaciondocumentoconvenio_tipodocumentoid/any(o1:(o1/_rs_tipoconvenioid_value eq " +
        agreementTypeId +
        " and o1/_rs_tipotramiteid_value eq null)))",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_id,
        name: elem.rs_name
      });
    });

    return data;
  }
  public async getAllOthers(agreementTypeId: string, procedureTypeId: string): Promise<CatalogueResponse[]> {
    const res = await this.get<CatalogueResponse, AxiosResponse<ODataMultipleResponse<ADODataResponse[]>>>(
      "d365api/v1/rs_tipodocumentos?$select=rs_id,rs_name&$expand=rs_tipodocumento_rs_parametrizaciondocumentoconvenio_tipodocumentoid" +
        "($filter=(_rs_tipoconvenioid_value eq " +
        agreementTypeId +
        " and _rs_tipotramiteid_value eq " +
        procedureTypeId +
        "))&" +
        "$filter=(rs_tipodocumento_rs_parametrizaciondocumentoconvenio_tipodocumentoid/any(o1:(o1/_rs_tipoconvenioid_value eq " +
        agreementTypeId +
        " and o1/_rs_tipotramiteid_value eq " +
        procedureTypeId +
        ")))",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: CatalogueResponse[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        id: elem.rs_id,
        name: elem.rs_name
      });
    });

    return data;
  }

  public async getAllAgreementDocuments(): Promise<AgreementDocuments[]> {
    const res = await this.get<AgreementDocuments, AxiosResponse<ODataMultipleResponse<AgreementDocumentsODataResponse[]>>>(
      "d365api/v1/rs_parametrizaciondocumentoconvenios?$select=rs_diasvigencia,_rs_tipoconvenioid_value,_rs_tipodocumentoid_value,_rs_tipotramiteid_value",
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: AgreementDocuments[] = [];
    res.data.value.forEach((elem) => {
      data.push({
        validityDay: elem.rs_diasvigencia,
        agreementTypeId: elem._rs_tipoconvenioid_value,
        documentTypeId: elem._rs_tipodocumentoid_value,
        procedureTypeId: elem._rs_tipotramiteid_value
      });
    });
    return data;
  }

  public async getRetrieveStatusAttributeMetadata(entityName: string): Promise<StatusAttributeResponse[]> {
    const res = await this.get<StatusAttributeODataResponse, AxiosResponse<StatusAttributeODataResponse>>(
      `d365api/v1/RetrieveStatusAttributeMetadata?entityName=${entityName}&attributeName=statuscode`,
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    const data: StatusAttributeResponse[] = [];
    if (res.data?.OptionSet?.Options != null && res.data.OptionSet.Options.length > 0) {
      res.data.OptionSet.Options.forEach((elem) => {
        data.push({
          value: elem.Value,
          name: elem.Label?.UserLocalizedLabel?.Label
        });
      });
    }
    return data;
  }
}

export const catalogueService = new CatalogueService();
